import { create } from 'zustand';
import { TIMBRE_PRESETS, type TimbrePreset } from '@engine/audio';

/** One player-editable tone slot. Mirrors the TuningSet pattern so the
 *  synth flow reads the same: slots row → preset palette → editors. */
export interface ToneSlot extends TimbrePreset {
  name: string;
}

interface SynthState {
  slots: ToneSlot[];
  activeSlotIndex: number;
  setActiveSlot: (i: number) => void;
  /** Cycle to the next slot (wraps). Used by the right-hand gesture. */
  cycleSlot: () => void;
  updateSlot: (i: number, patch: Partial<ToneSlot>) => void;
  applyPresetToActive: (presetKey: string) => void;
  addSlot: () => void;
  removeSlot: (i: number) => void;
  renameSlot: (i: number, name: string) => void;
}

const DEFAULT_SLOTS: ToneSlot[] = TIMBRE_PRESETS.map((p, i) => ({
  ...p,
  name: `Tone ${i + 1}`,
}));

// Synth slots are intentionally NOT persisted — per user preference,
// every session starts with the default tone palette.
export const useSynthStore = create<SynthState>()(
    (set) => ({
      slots: DEFAULT_SLOTS,
      activeSlotIndex: 0,
      setActiveSlot: (i) =>
        set((s) => ({
          activeSlotIndex: Math.max(0, Math.min(s.slots.length - 1, i)),
        })),
      cycleSlot: () =>
        set((s) => ({
          activeSlotIndex: (s.activeSlotIndex + 1) % s.slots.length,
        })),
      updateSlot: (i, patch) =>
        set((s) => {
          const next = s.slots.slice();
          next[i] = { ...next[i]!, ...patch };
          return { slots: next };
        }),
      applyPresetToActive: (presetKey) =>
        set((s) => {
          const preset = TIMBRE_PRESETS.find((p) => p.key === presetKey);
          if (!preset) return s;
          const next = s.slots.slice();
          const prev = next[s.activeSlotIndex]!;
          next[s.activeSlotIndex] = { ...preset, name: prev.name };
          return { slots: next };
        }),
      addSlot: () =>
        set((s) => {
          const src = s.slots[s.activeSlotIndex]!;
          const n = s.slots.length;
          return {
            slots: [...s.slots, { ...src, name: `Tone ${n + 1}` }],
            activeSlotIndex: n,
          };
        }),
      removeSlot: (i) =>
        set((s) => {
          if (s.slots.length <= 1) return s;
          const next = s.slots.filter((_, j) => j !== i);
          let idx = s.activeSlotIndex;
          if (i === idx) idx = Math.max(0, i - 1);
          else if (i < idx) idx -= 1;
          return { slots: next, activeSlotIndex: idx };
        }),
      renameSlot: (i, name) =>
        set((s) => {
          const next = s.slots.slice();
          next[i] = { ...next[i]!, name: name.slice(0, 24) };
          return { slots: next };
        }),
    }),
);

// Clear any previously persisted synth state from older builds.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('adt3.synth');
  } catch {
    /* ignore storage access errors */
  }
}
